import React from "react";
import { ScreenContext } from "./context";
import {
  Card,
  FlatActionList,
  Page,
  Stack,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import { ManageTowerPaymentsRootScreenParamList } from "../../../../types";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  formatDateToCalendar,
  fullDateTimeFormatSuccinct,
} from "@towersystems/roam-common/lib/shared-utils";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useFocusEffect } from "@react-navigation/native";
import { MUTATION_REMOVE_PAIRED_TOWER_PAYMENTS_DEVICE } from "../../../../common/queries";
import { View } from "react-native-ui-lib";

export interface ManageTowerPaymentsLandingScreenProps
  extends StackScreenProps<
    ManageTowerPaymentsRootScreenParamList,
    "ManageTowerPaymentsLandingScreen"
  > {}

export const ManageTowerPaymentsLandingScreen = ({
  navigation,
}: ManageTowerPaymentsLandingScreenProps) => {
  const context = React.useContext(ScreenContext);
  const transitionEnded = useHasTransitionEnded(navigation);
  const [performingAction, setPerformingAction] = React.useState(false);
  const flashMessagesContext = useFlashMessages();

  const { t } = useTranslation("settings");

  useFocusEffect(
    React.useCallback(() => {
      context?.refresh();
    }, [])
  );

  const [m] = useMutation<{
    removePairedTowerPaymentsDeviceByPaymentMethodId: boolean;
  }>(MUTATION_REMOVE_PAIRED_TOWER_PAYMENTS_DEVICE);

  const handleOnRemovePairedDevice = async () => {
    setPerformingAction(true);
    if (!context?.paymentMethod) {
      return;
    }
    try {
      await m({
        variables: {
          paymentMethodId: context.paymentMethod.id,
        },
      });
      flashMessagesContext.showMessage({
        severity: "success",
        message: t(
          "messages.remove_paired_tower_payments_device_success.message"
        ),
      });
      context?.refresh();
    } catch (error) {
      console.error("Failed to remove paired device:", error);
      flashMessagesContext.showMessage({
        severity: "error",
        message: t(
          "messages.remove_paired_tower_payments_device_error.message"
        ),
      });
    }
    setPerformingAction(false);
  };

  if (!context?.paymentMethod || !transitionEnded) {
    return (
      <Page>
        <FlatListSearchableSkeleton />;
      </Page>
    );
  }

  const pairedDevice = context.pairedDevice;
  const title = `Manage Tower Payments - ${context.paymentMethod.name}`;
  const secondaryTitle = "Manage your Tower Payments settings";

  const pairedDeviceContent = pairedDevice && (
    <Card title={`Paired Device: ${pairedDevice.deviceDescription}`}>
      <FlatActionList
        items={[
          {
            key: "paired-device",
            title: "Device description",
            componentRight: () => <Text>{pairedDevice.deviceDescription}</Text>,
          },
          {
            key: "paired-at",
            title: "Paired at",

            componentRight: () => (
              <Text>
                {pairedDevice.createdAt
                  ? formatDateToCalendar(
                      pairedDevice.createdAt,
                      fullDateTimeFormatSuccinct
                    )
                  : "Unknown"}
              </Text>
            ),
          },
          {
            key: "status",
            title: "Pairing connection status",
            componentRight: () =>
              context.isValidConnection === undefined ? (
                <View />
              ) : context.isValidConnection === true ? (
                <Text>Valid connection</Text>
              ) : (
                <Text>Invalid</Text>
              ),
          },
          {
            key: "remove-paired-device",
            title: "Remove Paired Device",
            destructive: true,
            onPress: handleOnRemovePairedDevice,
            disabled: performingAction,
            subTitle:
              "Unpair the current Tower Payments Device. Note: this does not remove the pairing on the payment device itself.",
          },
        ]}
      />
    </Card>
  );

  const pairingContent = !pairedDevice && (
    <Card>
      <FlatActionList
        items={[
          {
            key: "pairing",
            title: "Pair Tower Payments Device",
            subTitle:
              "Pair a new Tower Payments Device with this payment method. Roam has no control over removing or changing the pairing code.",
            onPress: () => {
              navigation.navigate("PairTowerPaymentsDeviceScreen");
            },
          },
        ]}
      />
    </Card>
  );

  return (
    <Page title={title} secondaryTitle={secondaryTitle}>
      <Stack vertical>
        {pairedDeviceContent}
        {pairingContent}
      </Stack>
    </Page>
  );
};
